/**
 * An uploaded or created plate playing live in the browser — with the same
 * animation code its videos are drawn with — in under a second: no video to
 * wait for ("Preparing animation…", "Writing sample words…"). Plays over and
 * over (Pause holds the frame); with `words`, the sample words come into its panel. `look` tries a
 * motion, effects and a transition before they are saved.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FiLoader, FiPlay, FiPause } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { LivePlayer, applyLiveLook, serverPartOf } from './PlatePackage';

export const PlateLive = ({ plateId, words = false, look = null, version, poster, replay = 0, className = 'absolute inset-0' }) => {
  const t = useT();
  const [fetched, setFetched] = useState(null);
  const [error, setError] = useState('');
  const [playKey, setPlayKey] = useState(0);
  const [paused, setPaused] = useState(false);
  const lookKey = JSON.stringify(look || {});
  // Only a change that needs new pictures goes to the server; motion, effects
  // and the transition are applied here at once (applyLiveLook).
  const serverKey = JSON.stringify(serverPartOf(look) || {});
  const data = useMemo(() => applyLiveLook(fetched, look), [fetched, lookKey]); // eslint-disable-line
  // "Play again" (or a change to try): from the start, playing — the same player, nothing fetched.
  useEffect(() => { if (replay) { setPlayKey((k) => k + 1); setPaused(false); } }, [replay]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    // A change of look waits a moment (clicking through effects doesn't send each).
    const timer = setTimeout(async () => {
      try {
        // A look with a text area or blur areas goes in the body; a simple one as a link.
        const next = look && (look.box || look.blurs || look.font || look.effect || look.ink)
          ? await api.plateLiveLook(plateId, { words, ...look })
          : await api.plateLive(plateId, { words, ...(look || {}) });
        if (!cancelled) setFetched(next);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'error');
      }
    }, fetched ? 150 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [plateId, words, serverKey, version]);

  return (
    <div className={className}>
      {poster && !data && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {data && <LivePlayer data={data} playKey={playKey} paused={paused} onEnd={() => setPlayKey((k) => k + 1)} />}
      {data && !error && (
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-label={t(paused ? 'plateTemplates.live.play' : 'plateTemplates.live.pause')}
          title={t(paused ? 'plateTemplates.live.play' : 'plateTemplates.live.pause')}
          className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/65 hover:bg-black/85 text-white text-xs px-3 py-1"
        >
          {paused ? <FiPlay size={12} /> : <FiPause size={12} />} {t(paused ? 'plateTemplates.live.play' : 'plateTemplates.live.pause')}
        </button>
      )}
      {!data && !error && (
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5">
          <FiLoader size={11} className="animate-spin" /> {t('plateTemplates.live.loading')}
        </span>
      )}
      {error && <span className="absolute bottom-2 left-2 rounded-full bg-black/75 text-white text-[11px] px-2.5 py-0.5">{error}</span>}
    </div>
  );
};

export default PlateLive;
